/**
 * `wayai toolsets …` — the MCP surfaces a base publishes, composed from
 * first-class Actions. Top-level
 * (docs/reference/rekor-platform-contracts.md §3.2).
 */

import { Command } from 'commander';
import { expected } from '../../lib/expected.js';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, parseData, pathSegment, requireBase, splitList, toolsetMcpUrl } from '../helpers.js';
import { confirm } from '../../lib/utils.js';

function collect(value: string, previous: string[]): string[] {
  return [...previous, value];
}

function parseSpec(spec: string): { key: string; ops: string[] } {
  const [key, opsStr] = spec.split(':');
  if (!key || !opsStr) {
    throw expected(`Invalid spec '${spec}'. Expected format: name:op1,op2`);
  }
  return { key, ops: splitList(opsStr) };
}

/** A `--action` ref: `<action_id>` → {action}, or `<action_id>=<surface_name>` → {action, action_name}. */
export function parseActionRef(spec: string): Record<string, unknown> {
  const [action, actionName] = spec.split('=');
  if (!action) {
    throw expected(`Invalid --action '${spec}'. Expected an Action id, or <action_id>=<surface_name>.`);
  }
  return actionName ? { action, action_name: actionName } : { action };
}

function parseRelSpec(spec: string): Record<string, unknown> {
  const { key, ops } = parseSpec(spec);
  return { rel_type: key, operations: ops };
}

export function buildToolsetsCommand(): Command {
  const toolsets = new Command('toolsets').description('Manage MCP toolsets');

  toolsets
    .command('upsert <slug>')
    .description('Create or update an MCP toolset')
    .option('--name <name>', 'Toolset display name (required unless using --config)')
    .option('--description <desc>', 'Toolset description')
    .option(
      '--action <ref>',
      'Reference an Action by id, optionally renamed: <action_id> or <action_id>=<surface_name> (repeatable). Create Actions first with `wayai actions upsert`.',
      collect,
      [] as string[],
    )
    .option('--relationship <spec>', 'Relationship tool spec: rel_type:op1,op2 (repeatable)', collect, [] as string[])
    .option('--batch <spec>', 'Batch operation spec: record_type_or_rel:op1,op2 (repeatable)', collect, [] as string[])
    .option('--sql-query', 'Enable sql_query tool')
    .option(
      '--config <json>',
      'Full toolset config as JSON. A toolset composes first-class Actions by reference — its `actions` are refs {action: <action_id>, action_name?, description_override?}. Create the Actions with `wayai actions upsert` (each carries its own record_type, operation, and guard/shaping config: filterable_fields, writable_fields, precondition, binding). Use @file.json to read from file.',
    )
    .option(
      '--mint-token',
      'After upsert, also mint a toolset-bound (consumer) token for this base and print it (handy for sandbox-testing the preview toolset)',
    )
    .action(async function (
      this: Command,
      slug: string,
      opts: {
        name?: string;
        description?: string;
        action: string[];
        relationship: string[];
        batch: string[];
        sqlQuery?: boolean;
        config?: string;
        mintToken?: boolean;
      },
    ) {
      let body: Record<string, unknown>;
      if (opts.config) {
        // Full JSON config — the shape config lives on the first-class Actions
        // the toolset only references. `parseData` also resolves `@file.json`,
        // which the help text advertises.
        body = parseData(opts.config);
      } else {
        if (!opts.name) {
          throw expected('--name is required (or use --config for full JSON config)');
        }
        body = { name: opts.name, actions: opts.action.map(parseActionRef) };
        if (opts.description) body.description = opts.description;
        if (opts.relationship.length > 0) {
          body.relationships = opts.relationship.map(parseRelSpec);
        }
        if (opts.batch.length > 0) {
          const operations: Record<string, string[]> = {};
          for (const spec of opts.batch) {
            const parsed = parseSpec(spec);
            operations[parsed.key] = parsed.ops;
          }
          body.batch = { enabled: true, operations };
        }
        if (opts.sqlQuery) body.sql_query = true;
      }

      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const format = outputFormat(this);
      printOutput(await client.request('PUT', `/v1/${base}/toolsets/${pathSegment(slug, 'toolset slug')}`, body), format);

      // Convenience: mint a consumer token bound to the toolset just upserted.
      // The mint and the config write stay two distinct server operations; only
      // the second call is chained here, and the toolset now exists so the
      // binding resolves.
      if (opts.mintToken) {
        const tokenData = await client.request('POST', '/v1/tokens', {
          name: `${slug} consumer`,
          toolset_binding: { base_id: base, toolset_id: slug },
        });
        if (format === 'json') {
          printOutput(tokenData, format);
        } else {
          console.log('\nToolset-bound token:');
          printOutput(tokenData, format);
          console.log(`MCP URL: ${toolsetMcpUrl(slug)}`);
        }
      }
    });

  toolsets
    .command('get <slug>')
    .description('Get a toolset')
    .option('--resolved', 'Include resolved record_type schemas')
    .action(async function (this: Command, slug: string, opts: { resolved?: boolean }) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const path = opts.resolved
        ? `/v1/${base}/toolsets/${pathSegment(slug, 'toolset slug')}/resolved`
        : `/v1/${base}/toolsets/${pathSegment(slug, 'toolset slug')}`;
      printOutput(await client.request('GET', path), outputFormat(this));
    });

  toolsets
    .command('list')
    .description('List all toolsets')
    .action(async function (this: Command) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/toolsets`), outputFormat(this));
    });

  toolsets
    .command('delete <slug>')
    .description('Delete a toolset')
    .option('-y, --yes', 'Skip confirmation prompt')
    .action(async function (this: Command, slug: string, opts: { yes?: boolean }) {
      if (!opts.yes && !(await confirm(`Delete toolset "${slug}"? This cannot be undone.`))) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request('DELETE', `/v1/${base}/toolsets/${pathSegment(slug, 'toolset slug')}`);
      console.log('Deleted');
    });

  toolsets
    .command('url <slug>')
    .description('Get the MCP connection URL for a toolset')
    .action((slug: string) => {
      console.log(toolsetMcpUrl(slug));
    });

  return toolsets;
}
